import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MessageSquare, Send, RefreshCw, Inbox, User, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "अभी";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("hi-IN");
}

export default function ReporterChatPage() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  const loadThreads = () => api.get("/admin/reporter-messages").then((r) => setThreads(r.data)).catch(() => {});
  const loadThread = (sid) => api.get(`/admin/reporter-messages/${sid}`).then((r) => setThread(r.data));

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (selected) loadThread(selected); }, [selected]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [thread]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch { toast.error("अपलोड नहीं हुआ"); }
    finally { setUploading(false); }
  };

  const send = async () => {
    if ((!text.trim() && !imgUrl) || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reporter-messages/${selected}`, { content: text.trim(), image_url: imgUrl || null });
      setText(""); setImgUrl("");
      if (data.sms_fallback) toast.success("SMS fallback भेजा");
      await loadThread(selected); await loadThreads();
    } catch (e) { toast.error("संदेश नहीं भेजा जा सका"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="reporter-chat-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-blue-600"/>रिपोर्टर चैट</h1>
          <p className="text-sm text-slate-500 mt-1">फ़ील्ड रिपोर्टरों के साथ सीधी बातचीत — corrections, requests, feedback</p>
        </div>
        <button onClick={loadThreads} className="p-2 rounded-md border border-slate-200 hover:bg-slate-50" title="रीलोड" data-testid="chat-refresh"><RefreshCw className="w-4 h-4"/></button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-[280px_1fr] h-[70vh]">
        {/* Threads sidebar */}
        <div className="border-r border-slate-200 overflow-y-auto">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-600 font-bold flex items-center gap-2">
            <Inbox className="w-3.5 h-3.5"/>Inbox ({threads.length})
          </div>
          {threads.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm italic">कोई संदेश नहीं</div>
          ) : threads.map((t) => (
            <button key={t.staff_id} onClick={() => setSelected(t.staff_id)}
              className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 ${selected === t.staff_id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""}`}
              data-testid={`thread-${t.staff_id}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black shrink-0">{(t.reporter_name || "?")[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-[#0F172A] text-sm truncate flex-1">{t.reporter_name || t.staff_id}</div>
                  <div className="text-[10px] text-slate-500 shrink-0">{relTime(t.last_at)}</div>
                </div>
                <div className="text-xs text-slate-600 truncate">{t.last_sender === "admin" ? "आप: " : ""}{t.last_content}</div>
                {t.unread > 0 && <span className="inline-block mt-1 bg-red-500 text-white text-[10px] font-black rounded-full px-1.5 min-w-[16px] text-center">{t.unread}</span>}
              </div>
            </button>
          ))}
        </div>
        {/* Thread */}
        <div className="flex flex-col">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 italic">बाईं ओर से रिपोर्टर चुनें</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                <User className="w-4 h-4 text-slate-500"/>
                <div>
                  <div className="font-bold text-[#0F172A]">{thread.reporter.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{thread.reporter.staff_id} · {thread.reporter.designation}</div>
                </div>
              </div>
              <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-body">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-lg shadow-sm ${
                      m.sender === "admin" ? "bg-[#0F172A] text-white rounded-br-none" : "bg-white text-slate-800 border border-slate-200 rounded-bl-none"
                    }`}>
                      {m.image_url && <img src={m.image_url} alt="" className="mb-1.5 rounded max-w-full max-h-64 object-cover"/>}
                      {m.audio_url && (
                        <div className="mb-1.5">
                          <audio src={m.audio_url} controls className="h-9 w-full max-w-[260px]"/>
                          {m.audio_transcript && <div className="mt-1 text-[11px] italic opacity-80">📝 {m.audio_transcript}</div>}
                        </div>
                      )}
                      {m.content && <div className="text-sm whitespace-pre-line leading-relaxed">{m.content}</div>}
                      <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-slate-400" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleString("hi-IN")}</div>
                    </div>
                  </div>
                ))}
              </div>
              {imgUrl && (
                <div className="px-3 py-2 border-t border-slate-100 flex items-center gap-2 bg-slate-50">
                  <img src={imgUrl} alt="" className="w-14 h-14 rounded object-cover"/>
                  <span className="text-xs text-slate-600 flex-1">तस्वीर तैयार</span>
                  <button onClick={() => setImgUrl("")} className="p-1 hover:bg-slate-200 rounded"><X className="w-4 h-4"/></button>
                </div>
              )}
              <div className="p-3 border-t border-slate-200 flex gap-2 items-center">
                <label className="p-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer" title="तस्वीर संलग्न करें">
                  <Paperclip className={`w-4 h-4 ${uploading ? "text-amber-500 animate-pulse" : "text-slate-600"}`}/>
                  <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="chat-attach"/>
                </label>
                <VoiceNoteRecorder
                  uploadFn={async (blob) => {
                    const fd = new FormData();
                    fd.append("file", new File([blob], `voice-${Date.now()}.webm`, { type: blob.type }));
                    const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    return { url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` };
                  }}
                  onReady={async (url) => {
                    await api.post(`/admin/reporter-messages/${selected}`, { audio_url: url });
                    await loadThread(selected); await loadThreads();
                    toast.success("आवाज़ भेजी");
                  }}
                />
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  placeholder="संदेश टाइप करें…"
                  className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="chat-input"/>
                <button onClick={send} disabled={busy || (!text.trim() && !imgUrl)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 rounded-md flex items-center gap-1.5 disabled:opacity-50" data-testid="chat-send">
                  <Send className="w-4 h-4"/>भेजें
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
